import "./rich-report-button.scss";
import { observer } from "mobx-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { IReportFiltersType, REPORT_TYPES } from "../../services/api/report-service";
import ReportStore from "../../stores/report-store";
import IconDownload from "../../components/icons/icon-download/icon-download";
import RoundLoader from "../../components/loaders/round-loader/round-loader";
import FontBody1 from "../../components/fonts/font-body1/font-body1";
import { FONT_COLOR } from "../../components/fonts/font-props";
import IconDropDownSmallArrow from "../../components/icons/icon-drop-down-small-arrow/icon-drop-down-small-arrow";
import IconDropDownUpSmallArrow from "../../components/icons/icon-drop-down-up-small-arrow/icon-drop-down-up-small-arrow";
import useDashboardTranslation from "../../dashboard-translation/hooks/dashboard-translation-hook";
import RichReportExpandedBox from "./rich-report-expanded-box";

interface IRichReportProps {
    type: REPORT_TYPES;
    title?: string;
    getReportFilters?: () => IReportFiltersType;
}

const RichReportButton: React.FC<IRichReportProps> = observer(({ type, title, getReportFilters }) => {
    const store = useMemo<ReportStore>(() => new ReportStore(type, getReportFilters), [type, getReportFilters]);
    const { getTranslation } = useDashboardTranslation();

    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        store.load();
    }, [store]);

    const reference = useRef<HTMLSpanElement>(null);

    const isLoading = store.isGenerating || store.isLoading;

    const onClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        e.stopPropagation();
        if (!store.lastGenerated) {
            store.generateReport();
        } else {
            setIsExpanded(!isExpanded);
        }
    }

    const reportTitle = title || getTranslation('REPORT', 'dashboard-general');

    return (
        <div className="rich-report-container">
            <span className="rich-report-button" ref={reference} onClick={onClick}>
                <span className={`rich-report-button-title ${isLoading ? 'loading' : ''}`}>
                    {isLoading ? <RoundLoader /> : <IconDownload />}
                    <FontBody1 color={isLoading ? FONT_COLOR.NEUTRAL_WHITE : FONT_COLOR.ACCENT1}>{reportTitle}</FontBody1>
                </span>
                {store.lastGenerated && (
                    <span className={`select-dropdown-indicator${isExpanded ? ' expanded' : ''}`}>
                        {isExpanded ? <IconDropDownUpSmallArrow color="var(--color-neutral-1-white)" /> : <IconDropDownSmallArrow />}
                    </span>)}
            </span>
            {(isExpanded && store.lastGenerated) && (
                <RichReportExpandedBox
                    refElement={reference.current}
                    isGeneratingReport={store.isGenerating}
                    onClick={() => {
                        setIsExpanded(false);
                    }}
                    onGenerateNewClick={() => {
                        setIsExpanded(false);
                        store.generateReport();
                    }}
                    report={store.lastGenerated}
                    reportStore={store}
                />
            )}
        </div>
    );
});

export default RichReportButton;